import type { AuthUser, MemberRole, OrganizationSummary } from '@viewer/protocol';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoginScreen } from '../auth/LoginScreen';
import { OrgScreen, orgTabsForRole, type OrgTab } from '../org/OrgScreen';
import { AccountTab } from './AccountTab';
import styles from './SettingsScreen.module.css';

export type SettingsTab = 'account' | OrgTab | 'shortcuts';

const ORG_TAB_LABEL: Record<OrgTab, string> = {
  overview: 'Visão geral',
  devices: 'Dispositivos',
  groups: 'Grupos',
  members: 'Colaboradores',
  sessions: 'Sessões',
  orgSettings: 'Opções da empresa',
};

interface Shortcut {
  keys: string[];
  name: string;
  help: string;
}

const SHORTCUTS: Shortcut[] = [
  {
    keys: ['Ctrl', 'Alt', 'Del'],
    name: 'Liberar mouse e teclado bloqueados',
    help: 'Quando quem acessa este computador ativa "Bloquear usuário", este atalho devolve o mouse e o teclado para você na hora. A conexão continua aberta.',
  },
];

interface Props {
  tab: SettingsTab;
  onTabChange(tab: SettingsTab): void;
  user: AuthUser | null;
  organizations: OrganizationSummary[];
  focusOrgId: string | null;
  canConnect: boolean;
  onConnect(code: string): void;
  onOrganizationsChanged(): Promise<void>;
  onAccountUpdated(user: AuthUser): void;
  onLogin(email: string, password: string): Promise<void>;
  onSignup(name: string, email: string, password: string): Promise<void>;
}

function isOrgTab(tab: SettingsTab): tab is OrgTab {
  return tab !== 'account' && tab !== 'shortcuts';
}

/** Configurações do aplicativo: conta, empresa e atalhos. */
export function SettingsScreen({
  tab,
  onTabChange,
  user,
  organizations,
  focusOrgId,
  canConnect,
  onConnect,
  onOrganizationsChanged,
  onAccountUpdated,
  onLogin,
  onSignup,
}: Props) {
  const [orgRole, setOrgRole] = useState<MemberRole | null>(null);
  const onRoleChange = useCallback((role: MemberRole | null) => setOrgRole(role), []);

  const tabs = useMemo(() => {
    const list: Array<{ id: SettingsTab; label: string }> = [{ id: 'account', label: 'Conta' }];
    if (user) {
      const orgTabs = orgRole ? orgTabsForRole(orgRole) : (['overview', 'devices', 'groups', 'members'] as OrgTab[]);
      for (const id of orgTabs) list.push({ id, label: ORG_TAB_LABEL[id] });
    }
    list.push({ id: 'shortcuts', label: 'Atalhos' });
    return list;
  }, [user, orgRole]);

  useEffect(() => {
    if (tabs.some((t) => t.id === tab)) return;
    onTabChange(user ? 'overview' : 'account');
  }, [tabs, tab, user, onTabChange]);

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Configurações</h1>
        <p className={styles.subtitle}>Conta, empresa e opções deste computador.</p>
      </div>

      <nav className={styles.tabs} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => onTabChange(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'account' &&
        (user ? (
          <AccountTab user={user} onUpdated={onAccountUpdated} />
        ) : (
          <LoginScreen
            onLogin={onLogin}
            onSignup={onSignup}
            intro="Entre para gerenciar sua conta, empresas e computadores liberados para você."
          />
        ))}

      {user && isOrgTab(tab) && (
        <OrgScreen
          organizations={organizations}
          focusId={focusOrgId}
          userId={user.id}
          canConnect={canConnect}
          tab={tab}
          onTabChange={onTabChange}
          onConnect={onConnect}
          onOrganizationsChanged={onOrganizationsChanged}
          onRoleChange={onRoleChange}
        />
      )}

      {tab === 'shortcuts' && (
        <section className={styles.panel}>
          <ul className={styles.list}>
            {SHORTCUTS.map((shortcut) => (
              <li key={shortcut.name} className={styles.row}>
                <span className={styles.rowMain}>
                  <span className={styles.rowName}>{shortcut.name}</span>
                  <span className={styles.rowHelp}>{shortcut.help}</span>
                </span>
                <span className={styles.keys}>
                  {shortcut.keys.map((key, index) => (
                    <span key={key}>
                      {index > 0 && <span className={styles.plus}>+</span>}
                      <kbd className={styles.key}>{key}</kbd>
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
